import React, { useEffect, useId, useRef } from 'react';
import { X, Trash2, Plus } from 'lucide-react';
import type { SessionMetadata } from '../utils/sessionHistory';
import { formatSessionDate } from '../utils/sessionHistory';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatWidgetString } from '../utils/i18n';

interface SessionPanelProps {
  sessions: SessionMetadata[];
  activeSessionId: string | null;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onCreate: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  strings?: WidgetStrings;
}

const SessionPanel = React.memo(function SessionPanel({
  sessions,
  activeSessionId,
  containerRef,
  onClose,
  onCreate,
  onSelect,
  onDelete,
  strings = DEFAULT_WIDGET_STRINGS,
}: SessionPanelProps) {
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  return (
    <div
      className="absolute inset-0 bg-white z-50 flex flex-col"
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="text-sm font-semibold text-gray-900" id={titleId}>
          {strings.sessionsTitle}
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
          aria-label={strings.closeSessions}
        >
          <X className="w-4 h-4 text-gray-900" aria-hidden="true" />
        </button>
      </div>
      <div className="px-4 py-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onCreate}
          className="text-xs px-2 py-1 rounded border border-blue-600 text-blue-600 hover:bg-blue-50 focus:outline-2 focus:outline-blue-500 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" aria-hidden="true" />
          {strings.newSession}
        </button>
        <div className="text-xs text-gray-500">
          {formatWidgetString(strings.sessionsTotal, { count: sessions.length })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
        {sessions.length === 0 ? (
          <div className="text-xs text-gray-500 text-center mt-4">{strings.noSessionsYet}</div>
        ) : (
          sessions.map(session => {
            const active = session.id === activeSessionId;
            return (
              <div
                key={session.id}
                className={`border rounded-lg p-3 flex items-start justify-between gap-3 cursor-pointer ${
                  active ? 'border-gray-900 bg-gray-100' : 'border-gray-200 hover:bg-gray-100'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelect(session.id)}
                  className="flex-1 min-w-0 text-left focus:outline-2 focus:outline-blue-500 rounded"
                  aria-current={active ? 'true' : undefined}
                >
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {session.title || strings.newConversation}
                  </div>
                  {session.preview && (
                    <div className="text-xs text-gray-600 mt-1">{session.preview}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    {formatSessionDate(session.lastModified)} ·{' '}
                    {formatWidgetString(strings.sessionMessageCount, {
                      count: session.messageCount,
                    })}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onDelete(session.id);
                  }}
                  className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
                  title={strings.deleteSession}
                  aria-label={strings.deleteSession}
                >
                  <Trash2 className="w-3.5 h-3.5 text-gray-600" aria-hidden="true" />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

export default SessionPanel;
